import type { FullMaterialSupportedComponent, MaterialComponentCategory } from 'api';
import { isObject, useApiMaterialsV1MaterialsSupportedComponentsRetrieve } from 'api';
import { useMemo } from 'react';

import { AutocompleteField, type AutocompleteFieldProps } from '../AutocompleteField/AutocompleteField';

type Props = Omit<AutocompleteFieldProps<FullMaterialSupportedComponent>, 'options' | 'defaultValue'> & {
  materialId?: number;
  materialColourId?: number;
  category?: MaterialComponentCategory;
  defaultValue?: FullMaterialSupportedComponent['id'];
  'data-testid'?: string;
};

export const MaterialSupportedComponentAutocomplete = ({
  materialId,
  materialColourId,
  category,
  defaultValue: _defaultValue,
  'data-testid': dataTestId = 'material-component-autocomplete',
  ...rest
}: Props) => {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {};
    if (materialColourId) {
      params.material_colour_id = materialColourId;
    }
    if (category) {
      params.material_component_category = category;
    }
    return params;
  }, [materialColourId, category]);

  const { data, isLoading } = useApiMaterialsV1MaterialsSupportedComponentsRetrieve(materialId || 0, queryParams, {
    query: {
      enabled: Boolean(materialId),
    },
  });

  return (
    <AutocompleteField<FullMaterialSupportedComponent>
      options={data?.results || []}
      loading={isLoading}
      getOptionLabel={(option) => {
        if (isObject(option) && option?.component?.name) {
          return option.component.name;
        }
        return '';
      }}
      valueField="id"
      data-testid={dataTestId}
      {...rest}
    />
  );
};
